import { useEffect, useState } from "react";
import { Box, Typography, Divider, Button, Grid, IconButton } from "@mui/material";
import { useQuery, useQueries } from "@tanstack/react-query";
import { HiOutlineTrash } from "react-icons/hi";
import {
  EDUCATION_INFORMATION_API,
  BASE_INFOS_API,
  EDUCATION_TYPE_API,
  FILTER_API,
} from "../../api";
import { useDispatch, useSelector } from "react-redux";
import {
  addEducationInformationData,
  setEducationInformationData,
  updateEducationInformationData,
  removeEducationInformationData,
  setIsLoading,
  setIsNewForEducationInformation,
  setReloadEducationInformationData,
} from "../../slice";
import { useEducationInfo } from "../../hooks";
import AddField from "./AddField";
import BaseTextArea from "../all-inputs/BaseTextArea";
import { TextInput, SelectBox, SwitchButton } from "../inputs";

function Education({ hasEditAccess, disabled = false, pageErrors = {} }) {
  const dispatch = useDispatch();
  const { data, reloadPage } = useSelector((state) => state.EducationInformation);
  const { userInfo } = useSelector((state) => state.UserCurrentState);
  const { handleSubmit: handleSubmitForm } = useEducationInfo({ hasEditAccess });

  const handleAdd = () => {
    dispatch(addEducationInformationData());
  };

  const handleSetDefaultValue = (data, index) => {
    let result = JSON.parse(JSON.stringify(data));
    result.forEach((info, i) => {
      if (i === index) {
        Object.keys(info).forEach((key) => {
          if (
            key !== "id" &&
            key !== "profileId" &&
            key !== "educationType" &&
            key !== "insideEducation"
          ) {
            result[i][key] = "";
          }
        });
      }
    });
    return result;
  };

  const { data: lookupsData } = useQuery(
    ["getFilterDataCollection_education"],
    () =>
      FILTER_API.GetFilterDataCollection(false, [17000, 14000, 15000, 19000, 18000, 16000, 31000]),
    {
      staleTime: Infinity,
    }
  );

  const [provinces, countries, firstEducationType, otherEducationType] = useQueries({
    queries: [
      {
        queryKey: ["getProvincesByCountryId", 1],
        queryFn: BASE_INFOS_API.GetProvincesByCountryId.bind(this, 1),
        staleTime: Infinity,
      },

      {
        queryKey: ["GetAllCountries", 1],
        queryFn: BASE_INFOS_API.GetAllCountries.bind(null, 1),
        staleTime: Infinity,
      },
      {
        queryKey: ["getEducationTypesDropDown", true],
        queryFn: EDUCATION_TYPE_API.GetEducationTypesDropDown.bind(null, true),
        staleTime: Infinity,
      },
      {
        queryKey: ["getEducationTypesDropDown", false],
        queryFn: EDUCATION_TYPE_API.GetEducationTypesDropDown.bind(null, false),
        staleTime: Infinity,
      },
    ],
  });

  const { refetch: getEducationInformation } = useQuery(
    ["getEducationInformation"],
    () => EDUCATION_INFORMATION_API.GetEducationInformation(),
    {
      onSuccess: ({ data: EducationInformation }) => {
        dispatch(setIsLoading(false));
        if (EducationInformation.success && EducationInformation.data?.length > 0) {
          dispatch(
            setEducationInformationData(EducationInformation.data ? EducationInformation.data : [])
          );
          dispatch(setIsNewForEducationInformation(false));
        }
        dispatch(setReloadEducationInformationData(false));
      },
      onError: () => {
        dispatch(setIsLoading(false));
      },
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (reloadPage) getEducationInformation();
  }, [reloadPage]);

  return (
    <form style={{ width: "100%" }}>
      {data.map((education, index) => (
        <Box
          position="relative"
          display="flex"
          flexDirection="column"
          width="100%"
          alignItems="flex-end"
          key={index}
        >
          {index !== 0 && (
            <IconButton
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                backgroundColor: "#EAEAEA",
                borderRadius: "0.6rem",
              }}
              disabled={disabled}
              onClick={() => dispatch(removeEducationInformationData(index))}
            >
              <HiOutlineTrash />
            </IconButton>
          )}
          <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
            <Grid
              container
              item
              xs={12}
              sm={12}
              md={6}
              lg={6}
              display="flex"
              flexDirection="row-reverse"
              alignItems="center"
              sx={{ ...Style.itemGrid, marginBottom: "1.2rem", paddingTop: "0.54rem" }}
            >
              <Grid xs={12} md={5} mb={{ xs: "1rem", md: 0 }}>
                <Typography
                  variant="h1"
                  textAlign="right"
                  sx={{ fontSize: "1.109rem", marginLeft: "1rem" }}
                >
                  {index === 0 ? "آخرین مدرک تحصیلی" : "مدرک تحصیلی"}
                </Typography>
              </Grid>
              <Grid xs={12} md={7}>
                <SwitchButton
                  label="در ایران تحصیل نکرده‌ام"
                  checked={!education.insideEducation}
                  disabled={disabled}
                  onChange={(event) => {
                    dispatch(setEducationInformationData(handleSetDefaultValue(data, index)));
                    dispatch(
                      updateEducationInformationData({
                        index,
                        key: "insideEducation",
                        value: !event.target.checked,
                        required: true,
                      })
                    );
                  }}
                />
              </Grid>
            </Grid>
            <SelectBox
              label="نوع تحصیلات"
              id="educationType"
              name="educationType"
              idKey="value"
              labelKey="text"
              data={
                index === 0
                  ? firstEducationType?.data?.data.data
                  : otherEducationType?.data?.data.data
              }
              disabled={disabled}
              value={education.educationType}
              onChange={(event) => {
                dispatch(setEducationInformationData(handleSetDefaultValue(data, index)));
                dispatch(
                  updateEducationInformationData({
                    index,
                    key: "educationType",
                    value: event.target.value,
                    required: true,
                  })
                );
              }}
              haveEmptyOption={false}
              error={`educationType_${index + 1}` in pageErrors}
            />
            {education.educationType?.toString() === "2" ? (
              <SelectBox
                label="وضعیت تحصیلی"
                data={lookupsData?.data?.data[16000]}
                id="seminaryEducationStatusCode"
                name="seminaryEducationStatusCode"
                disabled={disabled}
                idKey="code"
                value={education.seminaryEducationStatusCode}
                onChange={(event) =>
                  dispatch(
                    updateEducationInformationData({
                      index,
                      key: "seminaryEducationStatusCode",
                      value: event.target.value,
                      required: true,
                    })
                  )
                }
                error={`seminaryEducationStatusCode_${index + 1}` in pageErrors}
              />
            ) : (
              education.educationType?.toString() === "1" && (
                <SelectBox
                  label="وضعیت تحصیلی"
                  data={lookupsData?.data?.data[15000]}
                  id="educationStatusCode"
                  name="educationStatusCode"
                  disabled={disabled}
                  idKey="code"
                  value={education.educationStatusCode}
                  onChange={(event) =>
                    dispatch(
                      updateEducationInformationData({
                        index,
                        key: "educationStatusCode",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  error={`educationStatusCode_${index + 1}` in pageErrors}
                />
              )
            )}
            {education.educationType?.toString() === "1" && (
              <SelectBox
                label="مقطع تحصیلی"
                id="gradeCode"
                name="gradeCode"
                idKey="code"
                data={lookupsData?.data?.data[14000]}
                disabled={disabled}
                value={education.gradeCode}
                onChange={(event) =>
                  dispatch(
                    updateEducationInformationData({
                      index,
                      key: "gradeCode",
                      value: event.target.value,
                      required: true,
                    })
                  )
                }
                error={`gradeCode_${index + 1}` in pageErrors}
              />
            )}
            {education.educationType?.toString() === "1" && (
              <>
                <SelectBox
                  label="شاخه تحصیلی"
                  data={lookupsData?.data?.data[19000]}
                  idKey="code"
                  id="educationGroupCode"
                  name="educationGroupCode"
                  disabled={disabled}
                  value={education.educationGroupCode}
                  onChange={(event) =>
                    dispatch(
                      updateEducationInformationData({
                        index,
                        key: "educationGroupCode",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  error={`educationGroupCode_${index + 1}` in pageErrors}
                />
                <TextInput
                  placeholder="رشته تحصیلی"
                  label="رشته تحصیلی"
                  id="field"
                  name="field"
                  disabled={disabled}
                  value={education.field}
                  onChange={(event) =>
                    dispatch(
                      updateEducationInformationData({
                        index,
                        key: "field",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  error={`field_${index + 1}` in pageErrors}
                />
                {!education.insideEducation && (
                  <SelectBox
                    label="کشور محل تحصیل"
                    data={countries?.data?.data.data}
                    id="educationCountryId"
                    name="educationCountryId"
                    disabled={disabled}
                    value={education.educationCountryId}
                    onChange={(event) =>
                      dispatch(
                        updateEducationInformationData({
                          index,
                          key: "educationCountryId",
                          value: event.target.value,
                          required: true,
                        })
                      )
                    }
                    error={`educationCountryId_${index + 1}` in pageErrors}
                  />
                )}
                {education.insideEducation && (
                  <>
                    <SelectBox
                      label="استان محل تحصیل"
                      data={provinces?.data?.data.data}
                      id="educationProvinceId"
                      name="educationProvinceId"
                      disabled={disabled}
                      value={education.educationProvinceId}
                      onChange={(event) =>
                        dispatch(
                          updateEducationInformationData({
                            index,
                            key: "educationProvinceId",
                            value: event.target.value,
                            required: true,
                          })
                        )
                      }
                      error={`educationProvinceId_${index + 1}` in pageErrors}
                    />
                    <TextInput
                      label="شهر محل تحصیل"
                      placeholder="شهر محل تحصیل"
                      id="cityName"
                      name="cityName"
                      disabled={disabled}
                      value={education.cityName}
                      onChange={(event) =>
                        dispatch(
                          updateEducationInformationData({
                            index,
                            key: "cityName",
                            value: event.target.value,
                            required: true,
                          })
                        )
                      }
                      error={`cityName_${index + 1}` in pageErrors}
                      isSignIn={false}
                    />
                  </>
                )}
                {education.insideEducation && (
                  <SelectBox
                    label="نوع دانشگاه"
                    data={lookupsData?.data?.data[17000]}
                    id="universityTypeCode"
                    name="universityTypeCode"
                    idKey="code"
                    disabled={disabled}
                    value={education.universityTypeCode}
                    onChange={(event) =>
                      dispatch(
                        updateEducationInformationData({
                          index,
                          key: "universityTypeCode",
                          value: event.target.value,
                          required: true,
                        })
                      )
                    }
                    error={`universityTypeCode_${index + 1}` in pageErrors}
                  />
                )}
                <TextInput
                  placeholder="نام دانشگاه "
                  label="نام دانشگاه "
                  id="universityName"
                  name="universityName"
                  disabled={disabled}
                  value={education.universityName}
                  onChange={(event) =>
                    dispatch(
                      updateEducationInformationData({
                        index,
                        key: "universityName",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  error={`universityName_${index + 1}` in pageErrors}
                />
              </>
            )}
            {education.educationType?.toString() === "2" && (
              <>
                <SelectBox
                  label="سطح تحصیلی"
                  id="seminaryLevelCode"
                  name="seminaryLevelCode"
                  data={lookupsData?.data?.data[18000]}
                  disabled={disabled}
                  value={education.seminaryLevelCode}
                  onChange={(event) =>
                    dispatch(
                      updateEducationInformationData({
                        index,
                        key: "seminaryLevelCode",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  idKey="code"
                  error={`seminaryLevelCode_${index + 1}` in pageErrors}
                />
                {!education.insideEducation && (
                  <SelectBox
                    label="کشور محل تحصیل"
                    data={countries?.data?.data.data}
                    id="educationCountryId"
                    name="educationCountryId"
                    disabled={disabled}
                    value={education.educationCountryId}
                    onChange={(event) =>
                      dispatch(
                        updateEducationInformationData({
                          index,
                          key: "educationCountryId",
                          value: event.target.value,
                          required: true,
                        })
                      )
                    }
                    error={`educationCountryId_${index + 1}` in pageErrors}
                  />
                )}
                {education.insideEducation && (
                  <SelectBox
                    label="استان محل تحصیل"
                    data={provinces?.data?.data.data}
                    id="educationProvinceId"
                    name="educationProvinceId"
                    disabled={disabled}
                    value={education.educationProvinceId}
                    onChange={(event) =>
                      dispatch(
                        updateEducationInformationData({
                          index,
                          key: "educationProvinceId",
                          value: event.target.value,
                          required: true,
                        })
                      )
                    }
                    error={`educationProvinceId_${index + 1}` in pageErrors}
                  />
                )}
                <TextInput
                  label="شهر محل تحصیل"
                  placeholder="شهر محل تحصیل"
                  id="cityName"
                  name="cityName"
                  disabled={disabled}
                  value={education.cityName}
                  onChange={(event) =>
                    dispatch(
                      updateEducationInformationData({
                        index,
                        key: "cityName",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  isSignIn={false}
                  error={`cityName_${index + 1}` in pageErrors}
                />
                <TextInput
                  label="نام حوزه"
                  placeholder="نام حوزه"
                  id="seminarySchoolName"
                  name="seminarySchoolName"
                  disabled={disabled}
                  value={education.seminarySchoolName}
                  onChange={(event) =>
                    dispatch(
                      updateEducationInformationData({
                        index,
                        key: "seminarySchoolName",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  isSignIn={false}
                  error={`seminarySchoolName_${index + 1}` in pageErrors}
                />
                {userInfo.genderTypeEnum === 1 && (
                  <SelectBox
                    label="وضعیت ملبس بودن"
                    placeholder="وضعیت ملبس بودن"
                    id="turbansStatusCode"
                    name="turbansStatusCode"
                    data={lookupsData?.data?.data[31000]}
                    disabled={disabled}
                    value={education.turbansStatusCode}
                    onChange={(event) =>
                      dispatch(
                        updateEducationInformationData({
                          index,
                          key: "turbansStatusCode",
                          value: event.target.value,
                          required: true,
                        })
                      )
                    }
                    idKey="code"
                    error={`turbansStatusCode_${index + 1}` in pageErrors}
                  />
                )}
              </>
            )}
            <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
              <BaseTextArea
                label="توضیحات"
                id="description"
                name="description"
                flex={{ xs: 6.7, sm: 9.7, md: 9.6, lg: 9.6 }}
                labelFlex={{ xs: 5, sm: 2.5, md: 2.5, lg: 2.5 }}
                disabled={disabled}
                value={education.description}
                onChange={(event) =>
                  dispatch(
                    updateEducationInformationData({
                      index,
                      key: "description",
                      value: event.target.value,
                      required: true,
                    })
                  )
                }
                error={`description_${index + 1}` in pageErrors}
              />
            </Grid>
          </Grid>
          <Box width="100%" mt="1.3rem" mb="2rem">
            <Divider />
          </Box>
        </Box>
      ))}
      <Typography variant="h1" textAlign="right" ml="1.8rem">
        سایر مدارک
      </Typography>
      {!disabled && (
        <AddField
          handleAdd={handleAdd}
          title="ساير مدارك دانشگاهی/حوزوی/ دوره‌تخصصی خود را اضافه نماييد."
        />
      )}
      <Box
        width="100%"
        sx={{
          display: "flex",
          flexDirection: { xs: "row", sm: "row", md: "row" },
          justifyContent: "center",
          alignItems: "center",
          pt: 2,
          mt: "2rem",
        }}
      >
        <Button
          name="next"
          type="button"
          variant="m1"
          onClick={() => handleSubmitForm("/personal-info/financial-info")}
          sx={{ mr: 1, width: "165px", my: { xs: 1, sm: 0, md: 0 } }}
        >
          بعدی
        </Button>
        <Button
          name="previous"
          variant="m2"
          sx={{ mr: 1 }}
          onClick={() => handleSubmitForm("/personal-info/personal-info")}
        >
          قبلی
        </Button>
      </Box>
    </form>
  );
}

const Style = {
  itemGrid: {
    boxSizing: "border-box",
    paddingLeft: { xs: 0, sm: "0.5rem", md: "1rem", lg: "1rem" },
    marginBottom: "0.75rem",
  },
};

export default Education;
